import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";

import { FormError, PublicShell } from "@/auth/PublicShell";
import { TOKEN_ERRORS, humanise, resetPassword } from "@/auth/public_api";

/** Set a new password from an emailed link.
 *
 *  This is ALSO the invite landing page, not only a recovery one: a new user is
 *  created without a password (SEC-05 — nobody types a password on somebody
 *  else's behalf) and arrives here to choose their first one. Same screen, same
 *  token mechanism; the only difference is that the person has never signed in.
 *  That is why the copy says "Set your password" rather than "Reset".
 *
 *  Password policy text comes from the SERVER (`PASSWORD_POLICY` is
 *  `contextual_message: true` in the Fabric error catalogue) — the rules live
 *  in one place and the UI does not keep a second, drifting copy of them.
 */
export function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const mismatch = confirm.length > 0 && pw !== confirm;

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (mismatch) return;
    setBusy(true);
    setError("");
    try {
      await resetPassword(token, pw);
      setDone(true);
    } catch (err) {
      const e2 = err as { code: string; message?: string };
      setError(humanise(e2.code, e2.message, TOKEN_ERRORS));
    } finally {
      setBusy(false);
    }
  }

  if (done) {
    return (
      <PublicShell
        eyebrow="Orbit Platform"
        title="Password set"
        sub="You can sign in now. A completed reset also clears a lockout, so if you were locked out, you are not any more."
      >
        <Link to="/" className="btn-signin" style={{ textAlign: "center", display: "block" }}>
          Sign in →
        </Link>
      </PublicShell>
    );
  }

  // A missing token is not worth a round trip — but it IS the same sentence a
  // rejected one gets, because "no token" and "bad token" are the same fact to
  // the person holding a broken link.
  if (!token) {
    return (
      <PublicShell eyebrow="Orbit Platform" title="That link is incomplete" sub={TOKEN_ERRORS.TOKEN_INVALID}>
        <Link to="/forgot-password" className="btn-signin" style={{ textAlign: "center", display: "block" }}>
          Request a new link →
        </Link>
      </PublicShell>
    );
  }

  return (
    <PublicShell
      eyebrow="Orbit Platform"
      title="Set your password"
      sub="Choose a password for your Orbit account. This link works once."
    >
      <form onSubmit={submit}>
        <div className="igroup">
          <label htmlFor="rp-p">New password</label>
          <div style={{ position: "relative" }}>
            <input
              id="rp-p"
              className="inp"
              type={show ? "text" : "password"}
              style={{ paddingRight: 44 }}
              required
              value={pw}
              onChange={(e) => setPw(e.target.value)}
              autoComplete="new-password"
              autoFocus
            />
            <button
              type="button"
              className="pw-eye"
              aria-label={show ? "Hide password" : "Show password"}
              onClick={() => setShow((v) => !v)}
            >
              👁
            </button>
          </div>
        </div>
        <div className="igroup">
          <label htmlFor="rp-c">Confirm password</label>
          <input
            id="rp-c"
            className="inp"
            type={show ? "text" : "password"}
            required
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            autoComplete="new-password"
          />
          {mismatch && <div className="cap" style={{ color: "var(--bad)", marginTop: 4 }}>The two passwords don&rsquo;t match.</div>}
        </div>
        {error && <FormError>{error}</FormError>}
        <button className="btn-signin" type="submit" disabled={busy || !pw || mismatch}>
          {busy ? "Saving…" : "Set password →"}
        </button>
      </form>
      <div style={{ textAlign: "center", marginTop: 14 }}>
        <Link to="/">Back to sign in</Link>
      </div>
    </PublicShell>
  );
}
